import moment from "moment";
import {
  CategoryScale,
  Chart as ChartJS,
  Chart,
  Interaction,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  registerables,
  Tooltip,
} from "chart.js";
import { CrosshairPlugin, Interpolate } from "chartjs-plugin-crosshair";

// Register chart components
ChartJS.register(
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement
);
Chart.register(LineController, CrosshairPlugin, ...registerables);

Interaction.modes.interpolate = Interpolate;

export default {
  data() {
    return {
      tabTitle: "POWER CURVE",
      hoverElevationDate: "00:00:00",
      hoverElevationTime: "0",
      hoverElevation: 0,
      hoverPower: 0,
      hoverSpeed: 0,
      hoverCadence: 0,
      hoverHeartRate: 0,
      firstGraphDate: "",
      secondGraphDate: "",
      compareDate: [],
      maxElevation: 0,
      avgElevation: 0,
      minElevation: 0,
      avgPower: 0,
      minPower: 0,
      maxPower: 0,
      avgSpeed: 0,
      maxSpeed: 0,
      avgHeartRate: 0,
      maxHeartRate: 0,
      minHeartRate: 0,
      avgCadence: 0,
      maxCadence: 0,
      powerGraphHop: 1,
      powerGraphGap: 15,
      minElevationY: 0,

      // Chart configuration objects
      chartDataDefault: {
        labels: [],
        datasets: [
          {
            label: "Power",
            data: [],
            fill: false,
            borderColor: "#ffffff",
            backgroundColor: "#ffffff",
            borderWidth: 2,
            tension: 0.5,
            bezierCurve: false,
          },
          {
            label: "Power",
            data: [],
            fill: false,
            borderColor: "#FF6C01",
            backgroundColor: "#FF6C01",
            borderWidth: 2,
          },
          {
            label: "Power",
            data: [],
            fill: false,
            borderColor: "#00B2E3",
            backgroundColor: "#00B2E3",
            borderWidth: 2,
          },
        ],
      },
      chartOptions: {
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
              title: function (tooltipItem) {
                return "Time: " + tooltipItem[0]?.raw?.x || "";
              },
            },
          },
          crosshair: false,
        },
        elements: {
          point: {
            radius: 0,
          },
        },
        scales: {
          x: {
            id: "x-axis-1",
            ticks: {
              color: "#E1E1E1",
              fontSize: "20px",
            },
            grid: {
              display: true,
              color: "#7979797D",
              borderDash: [8, 2],
              tickLength: 0,
            },
          },
          y: {
            grid: {
              display: true,
              color: "#7979797D",
              borderDash: [8, 2],
              tickLength: 0,
            },
            beginAtZero: true,
            ticks: {
              color: "#E1E1E1",
              fontSize: "20px",
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
      },
      chartDataWeightPerKg: {
        labels: [],
        datasets: [
          {
            label: "Watt/Kg",
            data: [],
            fill: false,
            borderColor: "#ffffff",
            backgroundColor: "#ffffff",
            borderWidth: 2,
          },
          {
            label: "Watt/Kg",
            data: [],
            fill: false,
            borderColor: "#FF6C01",
            backgroundColor: "#FF6C01",
            borderWidth: 2,
          },
          {
            label: "Watt/Kg",
            data: [],
            fill: false,
            borderColor: "#00B2E3",
            backgroundColor: "#00B2E3",
            borderWidth: 2,
          },
        ],
      },
      chartOptionsWeightPerKg: {
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            mode: "index",
            intersect: false,
            callbacks: {
              title: function (tooltipItem) {
                return "Time: " + tooltipItem[0]?.raw?.x || "";
              },
            },
          },
          crosshair: false,
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              color: "#E1E1E1",
              fontSize: "20px",
            },
            grid: {
              display: true,
              color: "#7979797D",
              borderDash: [8, 2],
              tickLength: 0,
            },
          },
          y: {
            grid: {
              display: true,
              color: "#7979797D",
              borderDash: [8, 2],
            },
            beginAtZero: true,
            ticks: {
              color: "#E1E1E1",
              fontSize: "20px",
              tickLength: 0,
            },
          },
        },
        elements: {
          point: {
            radius: 0,
          },
        },
        responsive: true,
        maintainAspectRatio: false,
      },
      modalChartDataGraphElevation: {
        labels: [],
        type: "line",
        datasets: [
          {
            label: "Elevation",
            data: [],
            fill: "start",
            showLine: true,
            borderColor: "#000000",
            backgroundColor: "#e2e6eb",
            borderWidth: 1,
          },
        ],
      },
      modalChartOptionsGraphElevation: {
        elements: {
          point: {
            radius: 0,
          },
        },
        onHover: this.chartHover,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              title: function (tooltipItem) {
                return (
                  "Distance: " +
                    (Math.floor(tooltipItem[0]?.raw?.x) / 1000).toFixed(2) +
                    " KM" || ""
                );
              },
              label: function (context) {
                return "Elevation: " + context?.raw.y + " M";
              },
            },
            mode: "nearest",
            intersect: true,
          },
          crosshair: {
            line: {
              color: "#000000",
              width: 1,
            },
            sync: {
              enabled: true,
              group: 1,
              suppressTooltips: false,
            },
            zoom: {
              enabled: false,
            },
          },
        },
        scales: {
          x: {
            beginAtZero: false,
            ticks: {
              fontSize: "20px",
              callback: function (val, index) {
                let parsedValue = (
                  parseFloat(this.getLabelForValue(val)) / 1000
                ).toFixed(2);
                return index % 12 === 0 ? parsedValue + " KM" : "";
              },
            },
          },
          y: {
            min: this.minElevationY,
            ticks: {
              fontSize: "20px",
              callback: function (val) {
                if (val <= 0) {
                  return val <= 0 && val % 20 === 0 ? val + " M" : "";
                }
                return val + " M";
              },
            },
          },
        },
      },
      modalChartDataGraphPower: {
        type: "line",
        labels: [],
        datasets: [
          {
            label: "Power",
            data: [],
            fill: false,
            borderColor: "#d034d3",
            backgroundColor: "#d034d3",
            borderWidth: 2,
          },
        ],
      },
      modalChartOptionsGraphPower: {
        elements: {
          point: {
            radius: 0,
          },
        },
        onHover: this.chartHover,
        scales: {
          x: {
            ticks: {
              fontSize: "20px",
            },
            display: false,
            beginAtZero: true,
          },

          y: {
            beginAtZero: true,
            ticks: {
              fontSize: "20px",
              callback: function (val) {
                return val + " W";
              },
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            mode: "nearest",
            intersect: true,
            callbacks: {
              title: function (tooltipItem) {
                return (
                  "Distance: " +
                    (Math.floor(tooltipItem[0]?.raw?.x) / 1000).toFixed(2) +
                    " KM" || ""
                );
              },
              label: function (context) {
                return "Power: " + context?.raw.y + " W";
              },
            },
          },
          crosshair: {
            line: {
              color: "#000000",
              width: 1,
            },
            sync: {
              enabled: true,
              group: 1,
              suppressTooltips: false,
            },
            zoom: {
              enabled: false,
            },
          },
        },
      },
      modalChartDataGraphSpeed: {
        labels: [],
        type: "line",
        datasets: [
          {
            label: "Speed",
            data: [],
            fill: false,
            borderColor: "#1da46e",
            backgroundColor: "#1da46e",
            borderWidth: 1,
          },
        ],
      },
      modalChartOptionsGraphSpeed: {
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            mode: "nearest",
            intersect: true,
            callbacks: {
              title: function (tooltipItem) {
                return (
                  "Distance: " +
                    (Math.floor(tooltipItem[0]?.raw?.x) / 1000).toFixed(2) +
                    " KM" || ""
                );
              },
              label: function (context) {
                return "Speed: " + context?.raw.y + " KM/H";
              },
            },
          },
          crosshair: {
            line: {
              color: "#000000",
              width: 1,
            },
            sync: {
              enabled: true,
              group: 1,
              suppressTooltips: false,
            },
            zoom: {
              enabled: false,
            },
          },
        },
        elements: {
          point: {
            radius: 0,
          },
        },
        scales: {
          x: {
            ticks: {
              fontSize: "20px",
            },
            display: false,
            beginAtZero: true,
          },
          y: {
            beginAtZero: true,
            ticks: {
              fontSize: "20px",
              callback: function (val) {
                return val + " KM/H";
              },
            },
          },
        },
        onHover: this.chartHover,
        responsive: true,
        maintainAspectRatio: false,
      },
      modalChartDataGraphHeartRate: {
        labels: [],
        type: "line",
        datasets: [
          {
            label: "Heart Rate",
            data: [],
            fill: false,
            borderColor: "#ff5c00",
            backgroundColor: "#ff5c00",
            borderWidth: 1,
          },
        ],
      },
      modalChartOptionsGraphHeartRate: {
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            mode: "nearest",
            intersect: true,
            callbacks: {
              title: function (tooltipItem) {
                return (
                  "Distance: " +
                    (Math.floor(tooltipItem[0]?.raw?.x) / 1000).toFixed(2) +
                    " KM" || ""
                );
              },
              label: function (context) {
                return "HeartRate: " + context?.raw.y + " BPM";
              },
            },
          },
          crosshair: {
            line: {
              color: "#000000",
              width: 1,
            },
            sync: {
              enabled: true,
              group: 1,
              suppressTooltips: false,
            },
            zoom: {
              enabled: false,
            },
          },
        },
        elements: {
          point: {
            radius: 0,
          },
        },
        scales: {
          x: {
            ticks: {
              fontSize: "20px",
            },
            display: false,
            beginAtZero: true,
          },
          y: {
            beginAtZero: true,
            ticks: {
              fontSize: "20px",
              callback: function (val) {
                return val + " BPM";
              },
            },
          },
        },
        onHover: this.chartHover,
        responsive: true,
        maintainAspectRatio: false,
      },
      modalChartDataGraphCadence: {
        labels: [],
        type: "line",
        datasets: [
          {
            label: "Cadence",
            data: [],
            fill: false,
            borderColor: "#00b2e3",
            backgroundColor: "#00b2e3",
            borderWidth: 1,
          },
        ],
      },
      modalChartOptionsGraphCadence: {
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            mode: "nearest",
            intersect: true,
            callbacks: {
              title: function (tooltipItem) {
                return (
                  "Distance: " +
                    (Math.floor(tooltipItem[0]?.raw?.x) / 1000).toFixed(2) +
                    " KM" || ""
                );
              },
              label: function (context) {
                return "Cadence: " + context?.raw.y + " RPM";
              },
            },
          },
          crosshair: {
            line: {
              color: "#000000",
              width: 1,
            },
            sync: {
              enabled: true,
              group: 1,
              suppressTooltips: false,
            },
            zoom: {
              enabled: false,
            },
          },
        },
        elements: {
          point: {
            radius: 0,
          },
        },
        onHover: this.chartHover,
        scales: {
          x: {
            ticks: {
              fontSize: "20px",
            },
            display: false,
            beginAtZero: true,
          },
          y: {
            beginAtZero: true,
            ticks: {
              fontSize: "20px",
              callback: function (val) {
                return val + " rpm";
              },
            },
          },
        },
        responsive: true,
        maintainAspectRatio: false,
      },
      instanceChart: null,
      instanceWeightChart: null,
      instanceElevation: null,
      instancePower: null,
      instanceSpeed: null,
      instanceHeartRate: null,
      instanceCadence: null,
    };
  },
  computed: {
    chartDataElevation() {
      return this.modalChartDataGraphElevation;
    },
    chartOptionsElevation() {
      return this.modalChartOptionsGraphElevation;
    },
    chartDataPower() {
      return this.modalChartDataGraphPower;
    },
    chartOptionsPower() {
      return this.modalChartOptionsGraphPower;
    },
    chartDataSpeed() {
      return this.modalChartDataGraphSpeed;
    },
    chartOptionsSpeed() {
      return this.modalChartOptionsGraphSpeed;
    },
    chartDataHeartRate() {
      return this.modalChartDataGraphHeartRate;
    },
    chartOptionsHeartRate() {
      return this.modalChartOptionsGraphHeartRate;
    },
    chartDataCadence() {
      return this.modalChartDataGraphCadence;
    },
    chartOptionsCadence() {
      return this.modalChartOptionsGraphCadence;
    },
    chartDataWeightPerKgCompute() {
      return this.chartDataWeightPerKg;
    },
    chartOptionsWeightPerKgCompute() {
      return this.chartOptionsWeightPerKg;
    },
    chartDataCompute() {
      return this.chartDataDefault;
    },
    chartOptionsCompute() {
      return this.chartOptions;
    },
  },
  methods: {
    chartHover(evt, activeEls, chart) {
      let indexData;
      if (
        activeEls.length === 0 ||
        chart.getElementsAtEventForMode(
          evt,
          "nearest",
          {
            intersect: true,
          },
          true
        ).length === 0
      ) {
        return;
      }

      const finalIndex = chart.getElementsAtEventForMode(
        evt,
        "nearest",
        {
          intersect: true,
        },
        true
      )[0].index;

      indexData =
        this.modalChartDataGraphElevation.datasets[0].data[finalIndex];
      this.hoverElevationDate = moment
        .utc(indexData["time"] * 1000)
        .format("HH:mm:ss");
      this.hoverElevationTime = moment
        .utc(indexData["time"] * 1000)
        .format("mm");
      this.hoverElevation = (parseFloat(indexData.x) / 1000).toFixed(2);

      indexData = this.modalChartDataGraphPower.datasets[0].data[finalIndex];
      this.hoverPower = indexData?.y.toFixed(2);

      indexData = this.modalChartDataGraphSpeed.datasets[0].data[finalIndex];
      this.hoverSpeed = indexData?.y.toFixed(2);

      indexData =
        this.modalChartDataGraphHeartRate.datasets[0].data[finalIndex];
      this.hoverHeartRate = indexData?.y.toFixed(2);

      indexData = this.modalChartDataGraphCadence.datasets[0].data[finalIndex];
      this.hoverCadence = indexData?.y.toFixed(2);
    },
    toDaysMinutesSeconds(seconds) {
      var duration = moment.duration(seconds, "seconds");

      var sec = duration.asSeconds();
      var min = duration.asMinutes();
      var hrs = duration.asHours();

      if (sec <= 60) {
        return Math.floor(sec) + "s";
      } else {
        if (min <= 60) {
          return Math.floor(min) + "mn";
        } else {
          return Math.floor(hrs) + "h";
        }
      }
    },
    selectTab(title) {
      this.tabTitle = title;
    },
    processActivityData(data, fileId, fileDate) {
      if (data.detail.length > 0) {
        const dist = data.detail
          .map((val) => Math.round(val.distance) + "KM")
          .filter((distance, index, arr) => arr.indexOf(distance) === index);

        this.selectedFileId = fileId;
        this.selectedDate = fileDate;
        this.modalChartDataGraphElevation["labels"] = dist;
        this.modalChartDataGraphPower["labels"] = dist;
        this.modalChartDataGraphSpeed["labels"] = dist;
        this.modalChartDataGraphHeartRate["labels"] = dist;
        this.modalChartDataGraphCadence["labels"] = dist;

        this.modalChartDataGraphElevation["datasets"][0]["data"] =
          data.detail.map((val, index) => ({
            x: val.distance,
            y: val.altitude,
            time: index,
          }));

        this.minElevationY = this.lodash
          .minBy(this.modalChartDataGraphElevation["datasets"][0]["data"], "y")
          .y.toFixed();

        this.modalChartDataGraphPower["datasets"][0]["data"] = data.detail.map(
          (val) => ({
            x: val.distance,
            y: val.power,
          })
        );

        this.maxPower = this.lodash
          .maxBy(this.modalChartDataGraphPower["datasets"][0]["data"], "y")
          .y.toFixed();
        this.minPower = this.lodash
          .minBy(this.modalChartDataGraphPower["datasets"][0]["data"], "y")
          .y.toFixed();
        this.avgPower = this.lodash
          .meanBy(this.modalChartDataGraphPower["datasets"][0]["data"], "y")
          .toFixed();

        this.modalChartDataGraphSpeed["datasets"][0]["data"] = data.detail.map(
          (val) => ({
            x: val.distance,
            y: val.speed,
          })
        );

        this.maxSpeed = this.lodash
          .maxBy(this.modalChartDataGraphSpeed["datasets"][0]["data"], "y")
          .y.toFixed();
        this.avgSpeed = this.lodash
          .meanBy(this.modalChartDataGraphSpeed["datasets"][0]["data"], "y")
          .toFixed();

        this.modalChartDataGraphHeartRate["datasets"][0]["data"] =
          data.detail.map((val) => ({
            x: val.distance,
            y: val.heartRate,
          }));

        this.maxHeartRate = this.lodash
          .maxBy(this.modalChartDataGraphHeartRate["datasets"][0]["data"], "y")
          .y.toFixed();

        this.minHeartRate = this.lodash
          .minBy(this.modalChartDataGraphHeartRate["datasets"][0]["data"], "y")
          .y.toFixed();
        this.avgHeartRate = this.lodash
          .meanBy(this.modalChartDataGraphHeartRate["datasets"][0]["data"], "y")
          .toFixed();

        this.modalChartDataGraphCadence["datasets"][0]["data"] =
          data.detail.map((val) => ({
            x: val.distance,
            y: val.cadence,
          }));

        this.maxCadence = this.lodash
          .maxBy(this.modalChartDataGraphCadence["datasets"][0]["data"], "y")
          .y.toFixed();
        this.avgCadence = this.lodash
          .meanBy(this.modalChartDataGraphCadence["datasets"][0]["data"], "y")
          .toFixed();

        return true;
      }
      return false;
    },
    updateChartData(powerData, powerWeightData) {
      let timeArr = powerData?.map((val) => val.x);
      this.chartDataDefault["labels"] = timeArr;
      this.chartDataWeightPerKg["labels"] = timeArr;
      this.chartDataDefault["datasets"][0]["data"] = powerData;
      this.chartDataWeightPerKg["datasets"][0]["data"] = powerWeightData;
    },
    resetChartData() {
      this.chartDataWeightPerKg["datasets"][1]["data"] = [];
      this.chartDataDefault["datasets"][1]["data"] = [];
      this.chartDataWeightPerKg["datasets"][2]["data"] = [];
      this.chartDataDefault["datasets"][2]["data"] = [];
      this.firstGraphDate = "";
      this.secondGraphDate = "";
    },
  },
};
